import {Component, OnDestroy, OnInit, TemplateRef, Type, ViewChild} from '@angular/core';
import {MsalBroadcastService, MsalService} from '@azure/msal-angular';
import {AuthenticationResult, EventMessage, EventType, InteractionStatus} from '@azure/msal-browser';
import {catchError, filter, map, concatMap, finalize} from 'rxjs/operators';
import {UserService} from '../services/user.service';
import {UserExtendedDTO} from '../models/user/user-b2c-flow-dto.model';
import {UserCompaniesResponseDTO, UserCompany} from '../models/user/user-companies-response-dto.model';
import {ActivatedRoute, Router} from '@angular/router';
import {CommonModule} from '@angular/common';
import {MatDividerModule} from '@angular/material/divider';
import {of} from 'rxjs/internal/observable/of';
import {NgxPermissionsModule, NgxPermissionsService} from 'ngx-permissions';
import {Modules} from '../enums/modules.enum';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatButtonModule} from '@angular/material/button';
import {MatDialog, MatDialogModule} from '@angular/material/dialog';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {CompaniesService} from '../services/companies.service';
import {OrgScopeStore} from '../services/org-scope-store';
import {ModulesService} from '../services/modules.service';
import {IModule} from '../models/module/IModule';
import {PermissionsService as BcPermissionsService} from '../services/permissions.service';
import {forkJoin, EMPTY, firstValueFrom, Subject} from 'rxjs';
import {switchMap, catchError as catchErrorOp, takeUntil, skip} from 'rxjs/operators';
import {MatSelectModule} from '@angular/material/select';
import {FormsModule} from '@angular/forms';
import {AuthComponent} from '../auth/auth.component';
import {MatIconModule} from '@angular/material/icon';
import {SortByPipe} from '../shared/pipes/sort-by.pipe';
import {TenantContextService} from '../services/tenant-context.service';
import {ReadOnlyService} from '../services/read-only.service';
import {MODULE_FLAG_META} from '../permissions/read-only-flags';

@Component({
    selector: 'app-home',
    imports: [CommonModule, MatDividerModule, MatProgressSpinnerModule, MatButtonModule, MatDialogModule, MatFormFieldModule, MatInputModule, NgxPermissionsModule, MatSelectModule, FormsModule, AuthComponent, MatIconModule, SortByPipe],
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, OnDestroy {
  @ViewChild('checkOrgNumberDialog') checkOrgNumberDialog!: TemplateRef<MatDialog>;
  @ViewChild('confirmCompanyJoinDialog') confirmCompanyJoinDialog!: TemplateRef<MatDialog>;
  @ViewChild('newCompanyDialog') newCompanyDialog!: TemplateRef<MatDialog>;
  @ViewChild('companySelectDialog') companySelectDialog!: TemplateRef<MatDialog>;
  @ViewChild('moduleSelectDialog') moduleSelectDialog!: TemplateRef<MatDialog>;
  loginDisplay = false;
  companyId!: number;
  isRegistrationOpen: boolean = false;
  selectedModule: number;
  companies: UserCompany[];
  /** Modules this user can actually enter — fills the first-login module dialog. */
  moduleOptions: IModule[] = [];
  activeDashboard: Type<unknown> | null = null;
  /** True while the first-login company/module dialogs are up — see leaveHomeForModule(). */
  private awaitingCompanySelection = false;

  /**
   * Set once this component is gone. getUserOrganization() resolves over the network and its
   * subscription outlives the component, so without this a late response can open a dialog whose
   * TemplateRef belongs to a destroyed view: it renders empty and every control is dead.
   */
  private destroyed = false;

  constructor(
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private userService: UserService,
    private permissionsService: NgxPermissionsService,
    public dialog: MatDialog,
    private companiesService: CompaniesService,
    private router: Router,
    private route: ActivatedRoute,
    private modulesService: ModulesService,
    private bcPermissionsService: BcPermissionsService,
    private tenantContext: TenantContextService,
    private readOnlyService: ReadOnlyService,
  ) {}

  ngOnInit(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
        concatMap((result: EventMessage) => {
          const payload = result.payload as AuthenticationResult;
          this.authService.instance.setActiveAccount(payload.account);
          return this.checkNewUser(this.authService.instance.getActiveAccount()?.idTokenClaims);
        }),
        // Both MSAL streams outlive this component. Without this every visit to Home left a live
        // subscription behind, and each one re-ran the companies + all-module-permissions chain
        // against Identity whenever MSAL went idle — N visits, N bursts, for the rest of the tab.
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.getUserOrganization();
        // this.setOnboardingPermissions();
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        if (this.loginDisplay) {
          this.getUserOrganization();
        } else {
          // this.authService.loginRedirect();
        }
      });

     const urlParams = new URLSearchParams(window.location.search);
     const moduleFromUrl = +(urlParams.get('module') || 0);
     if (moduleFromUrl) {
       this.landOnModule(moduleFromUrl);
     } else {
       // 0 means this tab has no module of its own — a tab that adopted another organization via
       // `?org=`, whose per-tab state was cleared. Land on Home (1) rather than rendering nothing;
       // if the user has no Home access there, the switcher's fallback moves them on.
       this.selectedModule = this.modulesService.getSelectedModule() || 1;
       this.loadDashboard(this.selectedModule);
     }

     // The module switcher now switches in place (no reload). Corporate, Invoicing and Compliance
     // all render on this route, so the router reuses this component and only `?module=` changes —
     // follow it, or the header would move to the new module over the old dashboard.
     this.route.queryParamMap.pipe(
       skip(1), // the current value was handled just above
       map(params => +(params.get('module') || 0)),
       filter(id => !!id && id !== this.selectedModule),
       takeUntil(this.destroy$)
     ).subscribe(id => this.landOnModule(id));
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
  }
  private readonly destroy$ = new Subject<void>();

  /**
   * Land this tab in the module pinned on the URL (`?module=`), falling back to the first one the
   * user can reach if that module is denied.
   *
   * The access probe reads this tab's cached permission flags, and a tab that has just adopted an
   * organization via `?org=` has none — applyOrgQueryParam cleared them and setOnboardingPermissions
   * hasn't answered yet. Probing an empty cache denies *every* module, so the switcher's pinned
   * `module: 1` was thrown away and getFirstAccessibleModule picked whichever module still had a
   * stale flag lying around. Take the pinned module at face value while the cache is cold: it is
   * Home, which is where the org switcher wants to land, and the module switcher is right there if
   * the user turns out to have no access to it.
   */
  private landOnModule(moduleId: number): void {
    if (!this.hasCachedModuleFlags()) {
      this.selectedModule = moduleId;
      this.modulesService.selectModule(moduleId);
      this.loadDashboard(moduleId);
      return;
    }
    this.modulesService.hasModuleAccess(moduleId).then(hasAccess => {
      if (hasAccess) {
        this.selectedModule = moduleId;
        this.modulesService.selectModule(moduleId);
        this.loadDashboard(moduleId);
      } else {
        this.modulesService.getFirstAccessibleModule().then(fallbackModule => {
          this.selectedModule = fallbackModule || 1;
          this.modulesService.selectModule(this.selectedModule);
          this.loadDashboard(this.selectedModule);
        });
      }
    });
  }

  /** True once this tab holds any module's permission flags — i.e. the probe above can be trusted. */
  private hasCachedModuleFlags(): boolean {
    return Object.values(MODULE_FLAG_META).some(meta => !!OrgScopeStore.getItem(meta.storageKey));
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  /**
   * Open one of this component's TemplateRef dialogs, unless the component is already gone.
   *
   * MatDialog happily renders a TemplateRef whose host view has been destroyed — the dialog
   * appears, but its bindings never update and its click handlers run against a dead instance.
   * That is the "select a company" box with an empty dropdown and a Confirm that does nothing.
   */
  private openOwnedDialog(template: TemplateRef<MatDialog>): void {
    if (this.destroyed) return;
    this.dialog.open(template);
  }

  /**
   * Lazily loads only the dashboard for the active module so the landing page
   * doesn't pull every module's bundle (corporate, both invoicing modules,
   * risk assessment + their heavy deps) into the initial download.
   */
  async loadDashboard(module: number): Promise<void> {
    switch (module) {
      case 1:
        this.activeDashboard = (await import('../corporate/corporate-home-table/corporate-home-table.component'))
          .CorporateHomeTableComponent;
        break;
      case 2:
        this.activeDashboard = (await import('../invoicing-module/components/dashboard/invoicing-dashboard.component'))
          .InvoicingDashboardComponent;
        break;
      case 3:
        this.activeDashboard = (await import('../risk-assement/risk-assessment.component'))
          .RiskAssessmentComponent;
        break;
      case 4:
        // HR has no home dashboard — it's the HR employee-list dashboard (admins) or the
        // user's own employee dashboard. Redirect instead of rendering here.
        this.activeDashboard = null;
        this.redirectToHrDashboard();
        break;
      case 5:
        // Reporting opens the reports page directly.
        this.activeDashboard = null;
        this.router.navigate(['/reports'], { queryParams: { module: 5 } });
        break;
      case 6:
        // Tax owns a workspace (the ported BC_Tax_Mod screens) rather than a home dashboard.
        // getFirstAccessibleModule() can land a TaxUser here even while the switcher tile still
        // reads "coming soon", so without this the tab renders empty.
        this.activeDashboard = null;
        this.leaveHomeForModule(['/tax'], 6);
        break;
      case 7:
        // Accounting has no home dashboard — it owns a workspace with its own sidebar. Any tab
        // that lands on '/' while Accounting is the active module (a new tab, or an organization
        // opened via the header switcher) must be sent there, or it renders an empty page.
        this.activeDashboard = null;
        this.leaveHomeForModule(['/accounting'], 7);
        break;
      case 13:
        // Inventory likewise owns a workspace rather than a home dashboard.
        this.activeDashboard = null;
        this.leaveHomeForModule(['/inventory'], 13);
        break;
      case 11:
        // Migration owns a workspace with its own sidebar; /migration redirects to the casework
        // Overview. Without this the tab lands on '/' with module=11, falls through to `default:`
        // and shows an empty page next to a header menu that plainly works — which reads as broken.
        this.activeDashboard = null;
        this.leaveHomeForModule(['/migration'], 11);
        break;
      case 15:
        // Custodis owns a workspace (its own shell + vault sidebar) rather than a home dashboard.
        // Without this a tab that lands on '/' with module=15 — a new tab, a refresh, or the
        // switcher's navigate-then-reload — falls through to `default:` and renders a blank page.
        this.activeDashboard = null;
        this.leaveHomeForModule(['/custodis'], 15);
        break;
      default:
        this.activeDashboard = null;
    }
  }

  /**
   * Navigate off the home page into a module that owns its own workspace — unless the first-login
   * company/module dialogs are still up.
   *
   * Those dialogs are TemplateRefs declared in this component's template. MatDialog keeps showing
   * them after HomeComponent is destroyed, but their bindings and click handlers die with it, so
   * the user is left staring at a dialog whose buttons do nothing. Waiting is also the right
   * behaviour on its own terms: the module shells resolve their data from the company being
   * chosen, so there is nothing to render until the user picks one. Confirming the dialog reloads
   * the app, and this runs again with a company set.
   *
   * Note: cases 4 (HR) and 5 (Reporting) navigate away unguarded and can hit the same trap on a
   * first login. Left as-is here rather than changed blind — they predate this and are untested.
   */
  private leaveHomeForModule(commands: any[], moduleId: number): void {
    if (this.awaitingCompanySelection || this.dialog.openDialogs.length > 0) return;
    // The two flags above only catch a dialog that is ALREADY up. loadDashboard() runs straight
    // from ngOnInit while getUserOrganization() is still in flight, so on a first login this fires
    // first, navigates away, and the company dialog then opens from a component that no longer
    // exists — an empty dropdown on top of the module the user never chose. No company means that
    // question is still unanswered, and the module shells have nothing to resolve their data from
    // anyway, so stay put: confirming the dialog reloads the app and this runs again with one set.
    if (!this.companiesService.managedCompanyId) return;
    this.router.navigate(commands, { queryParams: { module: moduleId } });
  }

  /** Send the user to the HR area: the employee-list dashboard for HR admins, otherwise
   *  their own employee dashboard. */
  private redirectToHrDashboard(): void {
    if (this.isHrAdmin()) {
      this.router.navigate(['/hr'], { queryParams: { module: 4 } });
      return;
    }
    this.userService.getCurrentUser(false).subscribe({
      next: (user) => {
        if (user?.userId) {
          this.router.navigate(['/employee', user.userId, 'profile-details'], { queryParams: { module: 4 } });
        }
      },
      error: () => { /* stay on home if the user can't be resolved */ }
    });
  }

  private isHrAdmin(): boolean {
    try {
      const raw = OrgScopeStore.getItem('hrPermissions');
      return !!raw && (JSON.parse(raw) as string[]).includes('HRAdmin');
    } catch {
      return false;
    }
  }

  checkNewUser(claims: any) {
    if (claims) {
      const user: UserExtendedDTO = {
        azureObjectId: claims.oid,
        email: claims.emails[0],
        firstName: claims.given_name,
        lastName: claims.family_name
      };

      return this.userService.createUserfromADb2c(user).pipe(
        catchError(error => {
          throw error; // rethrow the error
        })
      );
    } else {
      return of(null);
    }
  }

  // MSAL fires both `msalSubject$` (LOGIN_SUCCESS) and `inProgress$` (transitioning to None) on a
  // normal login, close enough together that both subscriptions in ngOnInit call
  // getUserOrganization(). Without this guard that ran the whole chain twice — the companies fetch
  // AND all 8 module-permission calls in setOnboardingPermissions(). Reset in `finalize` (not just
  // on success) so a genuine failure still allows the other subscription's call through as a retry.
  private loadingUserOrganization = false;

  getUserOrganization() {
    // Tenant gate: when the subdomain resolved to an organization that is suspended/archived,
    // block the session entirely rather than loading data from its database.
    if (this.tenantContext.isResolved && !this.tenantContext.isActive) {
      this.denyTenantAccess('This organization is not active. Please contact your administrator.');
      return;
    }

    if (this.loadingUserOrganization) return;
    this.loadingUserOrganization = true;

    // When this tab already knows its company — every load after the first — the permission load
    // does not depend on the companies answer, so it starts now, in parallel, instead of after it.
    // Startup used to be companies → permissions → user → roles, one round trip after another.
    const knownCompanyId = +(OrgScopeStore.getItem('managedCompanyId') ?? 0);
    if (knownCompanyId) {
      this.setOnboardingPermissions();
    }

    this.userService.getUserCompanies().pipe(
      finalize(() => { this.loadingUserOrganization = false; }),
      // The companies call runs against the tenant's Identity database. A failure here for a
      // resolved tenant means the signed-in user does not belong to this organization.
      catchError(err => {
        if (this.tenantContext.isResolved) {
          this.denyTenantAccess('Your account does not have access to this organization.');
          return EMPTY;
        }
        throw err;
      }),
      map((response: UserCompaniesResponseDTO) => {
        if (response.organizationId) {
          this.isRegistrationOpen = false;
          localStorage.setItem('organization', JSON.stringify(response.organizationId));
          const companyId = +(OrgScopeStore.getItem('managedCompanyId') ?? 0);
          if (companyId) {
            // Already started above when the company was known before this answer arrived.
            if (!knownCompanyId) this.setOnboardingPermissions();
            return;
          }
          this.companies = response.companies;
          // No switching (docs/multi-org): nobody is asked to pick an organisation to get in. The
          // tab starts in the user's master company, else their first, and every page then shows
          // all their organisations anyway. Until the master company is known this holds the first
          // one, so nothing below runs without a company.
          this.companiesService.setManagedCompanyId(response.companies[0]?.id);
          this.awaitingCompanySelection = true;
          this.adoptDefaultCompany(response.companies);
          return
        } else {
          // No organization for this user. On a resolved tenant subdomain the "find/join a company
          // by registration number" flow doesn't apply — the user simply isn't a member of this
          // organization, so deny access instead of prompting them to register a company. The
          // registration prompt only makes sense on the non-tenant (apex/app) host.
          if (this.tenantContext.isResolved) {
            this.denyTenantAccess('Your account does not have access to this organization.');
            return;
          }
          // this.setOnboardingPermissions();
          this.openOwnedDialog(this.checkOrgNumberDialog)
        }
      })
    ).subscribe();
  }

  private denyTenantAccess(message: string) {
    this.loginDisplay = false;
    this.tenantContext.clear();
    localStorage.removeItem('organization');
    // eslint-disable-next-line no-alert
    alert(message);
    this.authService.logoutRedirect({postLogoutRedirectUri: '/'});
  }

  /** @param onLoaded runs once the flags are in ngx-permissions — anything gated on them waits. */
  setOnboardingPermissions(onLoaded?: () => void) {
    const companyId = this.companiesService.managedCompanyId;

    // Role-based effective permissions for the active company. Fire-and-forget — the role system
    // runs alongside the legacy module flags during the cutover (Phase 7), so a failure here
    // shouldn't block the user from landing on the home page, and the page-level *hasPermission
    // directive falls back to "deny" until the cache fills. It needs only the user and the company,
    // so it runs alongside the module flags below rather than after them.
    this.userService.getCurrentUser(false).pipe(
      switchMap(user => this.bcPermissionsService.refreshEffectivePermissions(user.userId, companyId)),
      catchErrorOp(() => EMPTY)
    ).subscribe();

    forkJoin({
      onboarding: this.userService.getModulePermissions(Modules.Onboarding),
      corporate: this.userService.getModulePermissionsById(companyId, Modules.Corporate),
      invoicing: this.userService.getModulePermissionsById(companyId, Modules.Invoicing),
      risk: this.userService.getModulePermissionsById(companyId, Modules.Risk),
      hr: this.userService.getModulePermissionsById(companyId, Modules.HR),
      reporting: this.userService.getModulePermissionsById(companyId, Modules.Reporting),
      billing: this.userService.getModulePermissionsById(companyId, Modules.Billing),
      // Accounting and Inventory were absent from this list while they were coming-soon tiles.
      // Coming-soon modules skip the access check, so nothing noticed — but the moment they became
      // `available` their flags were required and never fetched, leaving them locked for users who
      // do hold the role. The empty-array fallbacks keep one module whose permissions endpoint is
      // missing from failing the whole forkJoin, which would deny every module at once.
      accounting: this.userService.getModulePermissionsById(companyId, Modules.Accounting)
        .pipe(catchError(() => of([] as string[]))),
      inventory: this.userService.getModulePermissionsById(companyId, Modules.Inventory)
        .pipe(catchError(() => of([] as string[]))),
      // Tax joined them when the ported BC_Tax_Mod workspace turned the tile `available`.
      tax: this.userService.getModulePermissionsById(companyId, Modules.Tax)
        .pipe(catchError(() => of([] as string[]))),
      // Migration likewise, once BC_Migration_Mod's UI was ported in.
      migration: this.userService.getModulePermissionsById(companyId, Modules.Migration)
        .pipe(catchError(() => of([] as string[]))),
      // Audit likewise, once BC_Audit_UI's audit-module was ported in.
      audit: this.userService.getModulePermissionsById(companyId, Modules.Audit)
        .pipe(catchError(() => of([] as string[]))),
      // Signatures and Custodis were the next two to make the same trip: both tiles are
      // `available` and gated on their flags, but neither was ever fetched here, so a user who
      // signed in and never switched company had no SignaturesUser/CustodisUser at all and found
      // both tiles grayed out however they were granted in Org Settings. Only the company-switch
      // path in HeaderComponent fetched Signatures, and nothing anywhere fetched Custodis.
      signatures: this.userService.getModulePermissionsById(companyId, Modules.Signatures)
        .pipe(catchError(() => of([] as string[]))),
      custodis: this.userService.getModulePermissionsById(companyId, Modules.Custodis)
        .pipe(catchError(() => of([] as string[]))),
    }).subscribe(({corporate, onboarding, invoicing, risk, hr, reporting, billing, accounting, inventory, tax, migration, audit, signatures, custodis}) => {
      this.permissionsService.loadPermissions(
        onboarding.concat(corporate, invoicing, risk, hr, reporting, billing, accounting, inventory, tax, migration, audit, signatures, custodis))
      // setScoped, not setItem: this is the sign-in path, so it also establishes the browser-wide
      // default a brand-new tab inherits. Without that, a tab opened straight onto a deep link
      // (the footer version link is target=_blank rel=noopener, so it gets no sessionStorage clone)
      // started with no flags at all and never ran this fetch — HomeComponent owns it and never
      // mounts on a deep link. On a tab that adopted another organization setScoped still writes
      // per-tab only, so nothing leaks across orgs.
      OrgScopeStore.setScoped('permissions', JSON.stringify(onboarding));
      OrgScopeStore.setScoped('corporatePermissions', JSON.stringify(corporate));
      OrgScopeStore.setScoped('invoicingPermissions', JSON.stringify(invoicing));
      OrgScopeStore.setScoped('riskPermissions', JSON.stringify(risk));
      OrgScopeStore.setScoped('hrPermissions', JSON.stringify(hr));
      OrgScopeStore.setScoped('reportingPermissions', JSON.stringify(reporting));
      OrgScopeStore.setScoped('billingPermissions', JSON.stringify(billing));
      // Keys must match MODULE_FLAG_META, which is what AppComponent reads back on the next load.
      OrgScopeStore.setScoped('accountingPermissions', JSON.stringify(accounting));
      OrgScopeStore.setScoped('inventoryPermissions', JSON.stringify(inventory));
      OrgScopeStore.setScoped('taxPermissions', JSON.stringify(tax));
      OrgScopeStore.setScoped('migrationPermissions', JSON.stringify(migration));
      OrgScopeStore.setScoped('auditPermissions', JSON.stringify(audit));
      OrgScopeStore.setScoped('signaturePermissions', JSON.stringify(signatures));
      OrgScopeStore.setScoped('custodisPermissions', JSON.stringify(custodis));

      // The Viewer (read-only) flags live in the arrays just written, so re-evaluate now instead of
      // waiting for the next navigation to notice them.
      this.readOnlyService.invalidate();

      onLoaded?.();
    })
  }

  checkOrganizationNumber(orgNumber: string) {
    this.companiesService.checkRegistrationNumber(orgNumber)
      .subscribe((number)=> {
        if (!number) {
          this.dialog.closeAll();
          this.dialog.open(this.newCompanyDialog);
        } else {
          this.companyId = number;
          this.dialog.closeAll();
          this.dialog.open(this.confirmCompanyJoinDialog);
        }
      })
  }

  onReject() {
    this.dialog.closeAll()
    this.dialog.open(this.checkOrgNumberDialog)
  }

  onConfirmJoin() {
    this.companiesService.requestJoinCompany(this.companyId).subscribe();
  }

  onCompanyCreate() {
    this.router.navigate(['/onboarding/company'], {
      queryParams: {access: true}
    });
    this.dialog.closeAll();
  }

  goToModules() {
    this.dialog.closeAll();
    this.openOwnedDialog(this.moduleSelectDialog);
    // The options depend on flags this call fetches, so they fill in once it lands — the dialog is
    // already on screen and bound to this live component, so the list updates in place.
    this.setOnboardingPermissions(() => this.loadModuleOptions());
  }

  /**
   * Every module this user can actually enter, for the first-login dialog.
   *
   * The dialog used to hard-code three options (Home / Invoicing / Compliance), so a user whose
   * only access was HR, Reporting, Tax, Accounting or Inventory was asked to pick a module and
   * offered none of theirs. Reading ModulesService — the same source the header switcher uses —
   * means a new module shows up here without another edit. Coming-soon tiles are excluded because
   * there is nothing to land on, and external ones (Billing) because they open another app.
   */
  private async loadModuleOptions(): Promise<void> {
    const modules = await firstValueFrom(this.modulesService.getModules());
    const withAccess = await Promise.all(modules.map(async m =>
      ({ module: m, allowed: await this.modulesService.hasModuleAccess(m.id) })));

    this.moduleOptions = withAccess
      .filter(x => x.allowed && x.module.status !== 'coming-soon' && !x.module.externalUrl)
      .map(x => x.module);
  }

  onCompanySelect(e: number) {
    this.companiesService.setManagedCompanyId(e);
  }

  /**
   * First sign-in in this browser: start in the master company (where the user's own timesheets
   * and leave live) when it is one of theirs, else the first, and reload onto the dashboard — the
   * same reload the old "Select a company" and module dialogs ended with, minus the questions.
   */
  private adoptDefaultCompany(companies: { id: number }[]): void {
    const ids = new Set((companies || []).map(c => c.id));
    this.userService.getCurrentUser(false).pipe(
      map(user => user?.managerCompanyId),
      catchError(() => of(null)),
    ).subscribe(masterId => {
      const id = masterId && ids.has(masterId) ? masterId : companies[0]?.id;
      if (id) this.companiesService.setManagedCompanyId(id);
      this.modulesService.selectModule(1);
      this.router.navigate([''], { queryParams: { module: 1 } }).then(() => window.location.reload());
    });
  }

  selectModule() {
    this.dialog.closeAll();
    this.modulesService.selectModule(this.selectedModule);
    this.router.navigate([''], { queryParams: { module: this.selectedModule } }).then(() => {
      window.location.reload();
    });
  }

  onModuleSelect($event: number) {
    this.dialog.closeAll();
    this.selectedModule = $event;
    this.modulesService.selectModule(this.selectedModule);
    if (this.selectedModule === 3) {
      setTimeout(() => {
        this.router.navigate([''], { queryParams: { module: this.selectedModule } }).then(() => {
          window.location.reload();
        });
      }, 1000)
    } else {
      this.router.navigate([''], { queryParams: { module: this.selectedModule } }).then(() => {
        window.location.reload();
      });
    }
  }
}
